import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SendIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { fetchFederalRegisterCommentPeriods } from "../../utils/federalRegisterApi";
import { theme } from "../../theme/colors";

const LEARN_MORE_URL = "https://www.federalregister.gov/reader-aids/using-federalregister-gov/participating-in-the-rulemaking-process";

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function OpenCommentCard() {
  const [commentData, setCommentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFederalRegisterCommentPeriods();
        if (!cancelled) setCommentData(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load");
          setCommentData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const openUrl = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <Box className="rounded-2xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm" sx={{ mb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CommentOutlinedIcon sx={{ color: theme.primary, fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600}>Open for Public Comment</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <CircularProgress size={18} sx={{ color: theme.primary }} />
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        </Box>
      </Box>
    );
  }

  const empty = error || commentData.length === 0;
  if (empty) {
    return (
      <Box className="rounded-2xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm" sx={{ mb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CommentOutlinedIcon sx={{ color: theme.primary, fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600}>Open for Public Comment</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No open comment periods at this time. Check back soon — new opportunities to comment on federal regulations are posted regularly.
        </Typography>
        <Button
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => openUrl(LEARN_MORE_URL)}
          sx={{ color: theme.primary, textTransform: "none", fontWeight: 600 }}
        >
          Learn about public comments
        </Button>
      </Box>
    );
  }

  const primary = commentData[0];
  const additional = commentData.slice(1);

  return (
    <Box className="rounded-2xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm" sx={{ mb: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", display: "block", mb: 1 }}>
        Showing comment periods closing soonest
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <CommentOutlinedIcon sx={{ color: theme.primary, fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={600}>Open for Public Comment</Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 1 }}>{primary.title}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
        <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">Comments close {formatDate(primary.closeDate)}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon sx={{ color: "#fff" }} />}
          onClick={() => openUrl(primary.submitUrl)}
          sx={{
            background: theme.gradient,
            color: "#fff",
            "&:hover": { opacity: 0.95 },
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          Submit Comment
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => openUrl(primary.frUrl)}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            color: "#0f172a",
            borderColor: "#0f172a",
            bgcolor: "transparent",
            "&:hover": {
              borderColor: "#0f172a",
              bgcolor: "rgba(0,0,0,0.04)"
            }
          }}
        >
          View Document
        </Button>
      </Box>
      {additional.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setExpanded((e) => !e)}
            sx={{
              textTransform: "none",
              color: theme.primary,
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: "transparent",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
            }}
          >
            {additional.length} more open for comment {expanded ? "▲" : "▼"}
          </Button>
          {expanded && (
            <Box sx={{ mt: 1 }}>
              {additional.map((doc, i) => (
                <Box key={i} sx={{ py: 1.5, borderTop: "1px solid #e2e8f0" }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>{doc.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Closes {formatDate(doc.closeDate)}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => openUrl(doc.submitUrl)}
                      sx={{
                        textTransform: "none",
                        background: theme.gradient,
                        color: "#fff",
                        borderRadius: 2,
                        fontWeight: 600,
                        flexShrink: 0,
                        "&:hover": { opacity: 0.95 }
                      }}
                    >
                      Submit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openUrl(doc.frUrl)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        color: "#0f172a",
                        borderColor: "#0f172a",
                        bgcolor: "transparent",
                        flexShrink: 0,
                        "&:hover": {
                          borderColor: "#0f172a",
                          bgcolor: "rgba(0,0,0,0.04)"
                        }
                      }}
                    >
                      View Document
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
